import { Link } from "@tanstack/react-router";
import { PenLine } from "lucide-react";
import { ALL_ARTICLES, type IndexedArticle } from "@/lib/article-index.generated";
import { pickVariant, slugHash } from "@/lib/article-variation";

type Props = {
  /** Current article slug — drives the deterministic wording variant. */
  slug: string;
  /** Current article href, so we never link a page to itself. */
  href: string;
  category: string;
  /** Country slug for country guides; omit for cross-border/general articles. */
  country?: string;
  countryLabel?: string;
  className?: string;
};

const HEADINGS = [
  "Editor's note",
  "Why this matters here",
  "From the desk",
  "One thing we'd add",
  "Our take",
] as const;

/**
 * Picks the most useful neighbouring article: same category and country first,
 * then same category anywhere, then same country. Deterministic per slug so the
 * note is stable across builds.
 */
function pickNeighbour(
  href: string,
  category: string,
  country?: string,
): IndexedArticle | undefined {
  const pool = ALL_ARTICLES.filter((a) => a.href !== href);
  const tiers = [
    pool.filter((a) => a.category === category && a.country === country),
    pool.filter((a) => a.category === category),
    pool.filter((a) => a.country === country),
  ];
  for (const tier of tiers) {
    if (tier.length) return tier[slugHash(href, "neighbour") % tier.length];
  }
  return undefined;
}

/** Index titles are stored truncated ("… A Stude..."); trim the dangling fragment. */
function cleanTitle(title: string): string {
  if (!title.endsWith("...")) return title;
  // Drop the "..." and the word it cut in half, then any trailing punctuation.
  const words = title.slice(0, -3).trimEnd().split(" ");
  words.pop();
  return words.join(" ").replace(/[\s–—:,-]+$/, "");
}

/** Category-specific reason the detail matters to a LifeWestWay reader. */
function reasonFor(category: string, place: string, slug: string): string {
  const c = category.toLowerCase();
  if (c.includes("immigration") || c.includes("visa"))
    return pickVariant(slug, `reason-${c}`, [
      `Most of the readers who write to us about ${place} are juggling a visa clock and a bank balance at the same time, so treat the dates on this page as the fixed points and plan the money around them — not the other way round.`,
      `The rules here move faster than anything else we track for ${place}: a page that was accurate last quarter can be wrong today, which is why we re-check this one after every announced policy change rather than on a calendar.`,
    ] as const);
  if (c.includes("tax") || c.includes("finance") || c.includes("money"))
    return pickVariant(slug, `reason-${c}`, [
      `The number that trips people up in ${place} is almost never the headline rate — it's the threshold you cross mid-year after a move, so read this alongside your arrival date rather than in isolation.`,
      `We keep this figure separate from our visa pages on purpose: your residency status decides which of these rules applies to you, and getting that order wrong is the single most common mistake we see from readers in ${place}.`,
    ] as const);
  if (c.includes("job") || c.includes("career") || c.includes("work"))
    return pickVariant(slug, `reason-${c}`, [
      `Pay figures only tell half the story in ${place} — what your visa lets you work, and for how many hours, usually decides your real monthly income more than the hourly rate does.`,
      `Readers tend to arrive at this page from a job offer and leave with a visa question, so we've kept the employment rules and the sponsorship rules linked rather than buried in separate guides.`,
    ] as const);
  if (c.includes("health"))
    return pickVariant(slug, `reason-${c}`, [
      `The gap between landing in ${place} and being covered is where new arrivals get caught out; plan for that window before you plan for the long-term cover described below.`,
      `Healthcare access and residency paperwork are handled by different offices in ${place}, and the delay between them is the part worth budgeting for.`,
    ] as const);
  if (c.includes("housing") || c.includes("lifestyle") || c.includes("living"))
    return pickVariant(slug, `reason-${c}`, [
      `Rent is the easy number to find; deposits, upfront fees and the paperwork a landlord in ${place} expects from someone with no local credit history are the ones that decide whether a budget survives month one.`,
      `We price these guides for someone arriving without a local rental record, which is a very different cost picture from the averages you'll see quoted for ${place}.`,
    ] as const);
  if (c.includes("travel"))
    return pickVariant(slug, `reason-${c}`, [
      `We write travel pages for people who already live in ${place} or are about to, so the practicalities — timing, transport and what's worth a return trip — matter more here than a first-timer's checklist.`,
      `If you're visiting ${place} before committing to a move, use this as a scouting list: the cities below are also the ones our cost-of-living and jobs guides cover in most depth.`,
    ] as const);
  return pickVariant(slug, `reason-${c}`, [
    `We keep this page narrow on purpose — it answers one question about ${place} properly rather than skimming five, and links out where the next question starts.`,
    `The detail below matters most at the point where paperwork, timing and money overlap, which is exactly where readers moving to ${place} tell us they lose the most time.`,
  ] as const);
}

/**
 * A short, article-specific note from the editorial desk. It exists to give each
 * guide one or two lines of genuine context — why this particular detail matters
 * to someone using LifeWestWay — and to hand the reader the neighbouring guide
 * they usually need next.
 */
export function EditorNote({ slug, href, category, country, countryLabel, className = "" }: Props) {
  const neighbour = pickNeighbour(href, category, country);
  const place = countryLabel ?? "your destination country";
  const heading = pickVariant(slug, "note-heading", HEADINGS);
  const reason = reasonFor(category, place, slug);

  return (
    <aside
      aria-label="Editor's note"
      className={`not-prose my-8 rounded-xl border-l-4 border-accent bg-accent/[0.06] py-4 pl-5 pr-4 sm:pl-6 ${className}`}
    >
      <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
        <PenLine className="h-3.5 w-3.5" aria-hidden />
        {heading}
      </p>
      <p className="mt-2 text-sm leading-relaxed text-foreground/85 sm:text-base">
        {reason}
        {neighbour ? (
          <>
            {" "}
            If that's where you are right now, read this next to{" "}
            <Link
              to={neighbour.href}
              title={neighbour.title}
              className="font-semibold text-accent underline-offset-2 hover:underline"
            >
              {cleanTitle(neighbour.title)}
            </Link>
            .
          </>
        ) : null}
      </p>
    </aside>
  );
}

export default EditorNote;
